// One post-state model for the post page: the stage a person sees, whose turn
// it is, and the status banner's words. Pure and React-free on purpose, so the
// date rail, the banner and scripts/test-post-state.mjs all read the same
// answer. Only type imports: the test bundles this file on its own.
//
// Three stops (Figma 08, 12 Oct): Concept until the client approved the idea
// and its creative brief (they go out together and are answered once), then
// Approved while the files come in, then Live once the files are in. There is
// no production step: an approved post shows Add files, which opens Live.
// Client review, a failed run and a missed day are states inside a stop.

import type {
  CalendarReviewDecisionValue,
  CalendarSlotStatus,
  SlotOrigin,
} from "@/types/contentCalendar.type";
import type { CalendarBriefStatus } from "@/types/productionBrief.type";

export type PostStage = "concept" | "production" | "live";

/** Whose turn it is. Drives the colour of the turn dot and its words. */
export type TurnOwner = "you" | "client" | "agent" | "none" | "late";

/** lucide-react icon names the banner renders. Kept as names so this stays pure. */
export type BannerIcon =
  | "Sparkles"
  | "RotateCcw"
  | "PenLine"
  | "ListChecks"
  | "MessageSquare"
  | "Send"
  | "Clock"
  | "Check"
  | "CheckCheck"
  | "CircleCheck"
  | "CalendarX"
  | "Upload"
  | "Loader"
  | "Package"
  | "CircleHelp"
  | "Files"
  | "Link2Off"
  | "Users";

/** What the banner's one primary does. The page wires each to an existing flow. */
export type PrimaryAction =
  | "generate-ideas"
  | "retry-ideas"
  | "pick-idea"
  | "rework-idea"
  | "send-concept"
  | "retry-brief"
  | "send-brief"
  | "edit-brief"
  | "open-live"
  | "add-files"
  | "send-kit"
  | "copy-kit-link"
  | "move-post"
  | "remind"
  | "approve-for-client"
  | "schedule-all"
  | "mark-posted"
  | "reconnect"
  /** A channel that didn't post: try it again now (Figma 08, 13 Oct). */
  | "retry-post"
  // The client's page (client-portal/clientReviewModel).
  | "approve-concept"
  | "approve-brief";

export interface BannerPrimary {
  label: string;
  action: PrimaryAction;
  disabled?: boolean;
  /** Drawn as a quiet text button (the Figma's "Remind Fatafeat"), not the filled primary. */
  quiet?: boolean;
}

export interface PostBanner {
  icon: BannerIcon;
  title: string;
  meta: string;
  primary?: BannerPrimary;
  /** A second action beside the primary ("Approve for Fatafeat" beside "Remind again"). */
  secondary?: BannerPrimary;
  /** The title in alert ink (a late answer). */
  tone?: "alert";
}

export interface PostState {
  stage: PostStage;
  stageIndex: 0 | 1 | 2;
  /** Concept, approved, live; a quick post has two stops: files, live. */
  stops: 2 | 3;
  /** A quick post: no concept, no brief, and never "Approved" anywhere. */
  quick: boolean;
  /** `alert`: the turn is a failure (Missed, Didn't post) and takes the alert ink, never lime. */
  turn: { owner: TurnOwner; label: string; alert?: boolean };
  banner: PostBanner;
  /**
   * Whether the page shows the whose-turn banner. It stands at the handoffs
   * (send, with the client, changes asked, approved, day passed, brief failed)
   * and on Live. The ideas pages (ideas not written, being written, the pick, a
   * failed run) and the brief being written have none: those pages draw their
   * own waiting screen, and the primary moves to the page header.
   */
  showBanner: boolean;
}

/** The slice of a slot the model reads. Structural, so tests pass plain objects. */
export interface PostStateSlot {
  status: CalendarSlotStatus;
  origin?: SlotOrigin;
  ideaError?: string | null;
  updatedAt?: string;
  scheduledAt?: string;
  approvalSource?: "CLIENT" | "TEAM" | null;
  ideas?: Array<{ id: string; status: string; order?: number }>;
  reviewDecisions?: Array<{
    decision: CalendarReviewDecisionValue;
    reason: string | null;
    clientName?: string;
    decidedAt?: string;
  }>;
  /** The team's record of an approval given outside the app (backend #129). */
  outsideApproval?: {
    concept: { approvedByName: string; approvedAt: string } | null;
    brief?: { approvedByName: string; approvedAt: string } | null;
  } | null;
  /** The open send this post waits in (team read, #130). */
  clientReview?: {
    concept: {
      sentAt: string;
      answerBy: string | null;
      late: boolean;
      lastReminder?: { sentAt: string } | null;
      openedAt?: string | null;
    } | null;
  } | null;
  deliverable?: {
    status?: string;
    publications?: Array<{
      platform: string;
      status: string;
      scheduledAt?: string | null;
      publishedAt?: string | null;
      nextAttemptAt?: string | null;
    }>;
  } | null;
}

/** The slice of a brief the model reads (a BriefRailInfo or a full brief). */
export interface PostStateBrief {
  status: CalendarBriefStatus;
  version: number;
  sentVersion: number | null;
  acceptanceSource?: "CLIENT" | "TEAM" | null;
  generationError?: string | null;
  updatedAt?: string;
}

/**
 * The Live tab's state, for its banners (Figma 08 "Files, going out and
 * live"): files uploading, files in and how far each channel got. Passed only
 * where the page reads it (the Live tab, or a post already in Live); the
 * calendar's cards read the publications on the slot instead.
 */
export interface PostStateLive {
  uploading: number;
  uploadTotal: number;
  filesIn: number;
  unplaced: number;
  /** Creo's question as the banner's line, or null. */
  question: string | null;
  /** "unsupported" until the backend holds a kit. */
  kit: "none" | "ready" | "sent" | "unsupported";
  /** The first 24 hours are in: the results page has no banner. */
  results: boolean;
  /** Creo matches the files (the backend's `live` block is present). */
  matching: boolean;
  /** The post's channels and how far each got (the new Live banners); absent on an older read. */
  channels?: {
    total: number;
    /** Ready to go: a file, connected, nothing missing. */
    ready: number;
    scheduled: number;
    posting: number;
    live: number;
    /** The time the scheduled ones go out (ISO), the earliest. */
    goesOutAt: string | null;
    /** When the first channel went live (ISO). */
    liveSince: string | null;
    /** A channel that didn't post: its platform and the platform's words. */
    failed: { platform: string; reason: string | null; attempts: number; nextAttemptAt: string | null } | null;
    /** Channels whose account is not connected, by name ("Facebook"). */
    notConnected: string[];
    /** A connected account that needs reconnecting. */
    reconnect: { platform: string; handle: string | null } | null;
    /** A "post it yourself" channel whose time has come, by name. */
    selfDue: string | null;
    /** What one channel still needs, in a few words ("TikTok needs a cover"). */
    needs: string[];
    /** Pictures that go out as one carousel, when there are some. */
    carouselPictures: number;
  } | null;
}

export interface PostStateOptions {
  /**
   * `undefined` means "not asked" and `null` "asked, there is none". A picked
   * post without a brief row yet is still having it written.
   */
  brief?: PostStateBrief | null;
  /** The brand's client display name; falls back to "the client". */
  clientName?: string | null;
  isGuest: boolean;
  now: number;
  /** IDEATION only: the idea the person has selected on the board, if any. */
  selectedIdea?: { id: string; index: number } | null;
  /** When the concept went to the client (the latest shared version), if known. */
  conceptSentAt?: string | null;
  /** When the creative brief went to the client, if known. */
  briefSentAt?: string | null;
  /** The client's reason on a brief sent back, if known. */
  briefDeclineReason?: string | null;
  /** How many client comments came back with a change request, when known. */
  changeComments?: number | null;
  /** The brand's time zone for times on the cards ("Scheduled · Thu 18:00"). */
  timeZone?: string;
  /** The Live tab's state, where the page reads it. */
  live?: PostStateLive | null;
}

// ------------------------------------------------------------------ helpers

const CONTENT_FINAL: CalendarSlotStatus[] = ["SIGNED_OFF", "IN_CLIENT_REVIEW", "CLIENT_APPROVED"];
const DAY_MS = 24 * 60 * 60 * 1000;

export function clientLabel(name: string | null | undefined): string {
  const trimmed = name?.trim();
  return trimmed ? trimmed : "the client";
}

/** Sentence-initial form: "the client" becomes "The client"; a name is kept. */
export function clientTitle(name: string | null | undefined): string {
  const label = clientLabel(name);
  return label === "the client" ? "The client" : label;
}

/** Mirrors `openDeclineFor`: a client decline still waiting on the team. */
export function openDecline(slot: PostStateSlot) {
  const decision = slot.reviewDecisions?.[0];
  if (!decision || decision.decision !== "DECLINED") return null;
  return CONTENT_FINAL.includes(slot.status) ? null : decision;
}

/** A pipeline post has a quick-post twin with no Concept stage. */
export function isQuickPost(slot: PostStateSlot): boolean {
  return slot.origin === "DIRECT";
}

/** Short date for banner copy, "Thu 10 Dec". UTC, like every calendar day. */
export function shortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const weekday = date.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" });
  const day = date.getUTCDate();
  const month = date.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" }).replace("Sept", "Sep");
  return `${weekday} ${day} ${month}`;
}

/** "Mon 30 Nov, 10:42": a moment, in the viewer's (or the brand's) zone. */
export function dateTime(iso: string | null | undefined, timeZone?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const zone = timeZone ? { timeZone } : {};
  const day = date
    .toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", ...zone })
    .replace(",", "")
    .replace("Sept", "Sep");
  const clock = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, ...zone });
  return `${day}, ${clock}`;
}

/** "18:00" in the brand's zone. */
function clockOf(iso: string, timeZone?: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  });
}

/** "Thu 10 Dec" in the brand's zone. */
function shortDateIn(iso: string, timeZone?: string): string {
  const zone = timeZone ? { timeZone } : {};
  return new Date(iso)
    .toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", ...zone })
    .replace(",", "")
    .replace("Sept", "Sep");
}

/** "Thu 10 Dec, 18:00" in the brand's zone. */
function dayClock(iso: string, timeZone?: string): string {
  return `${shortDateIn(iso, timeZone)}, ${clockOf(iso, timeZone)}`;
}

/** "today 18:00", "Thu 18:00": when something goes out, on a card. */
function whenOnCard(iso: string, now: number, timeZone?: string): string {
  const zone = timeZone ? { timeZone } : {};
  const dayKey = (t: number) => new Date(t).toLocaleDateString("en-CA", zone);
  const clock = clockOf(iso, timeZone);
  const at = new Date(iso).getTime();
  if (dayKey(at) === dayKey(now)) return `today ${clock}`;
  if (dayKey(at) === dayKey(now + DAY_MS)) return `tomorrow ${clock}`;
  if (Math.abs(at - now) < 6 * DAY_MS) {
    return `${new Date(iso).toLocaleDateString("en-GB", { weekday: "short", ...zone })} ${clock}`;
  }
  return `${shortDateIn(iso, timeZone)} ${clock}`;
}

/**
 * Elapsed time in words, "12 min" / "1 h 5 min", at the minute resolution of
 * the stuck clock. (An "0:12" here read as twelve seconds. Live second-by-second
 * timers use ElapsedSince instead.)
 */
export function elapsedLabel(since: string | null | undefined, now: number): string | null {
  if (!since) return null;
  const start = new Date(since).getTime();
  if (Number.isNaN(start)) return null;
  const minutes = Math.max(0, Math.floor((now - start) / 60_000));
  if (minutes < 1) return "under a minute";
  if (minutes < 60) return `${minutes} min`;
  const rest = minutes % 60;
  return rest ? `${Math.floor(minutes / 60)} h ${rest} min` : `${Math.floor(minutes / 60)} h`;
}

/** "0:34": machine work so far, as the cards count it. */
export function clockSince(since: string | null | undefined, now: number): string | null {
  if (!since) return null;
  const start = new Date(since).getTime();
  if (Number.isNaN(start)) return null;
  const seconds = Math.max(0, Math.floor((now - start) / 1000));
  const minutes = Math.floor(seconds / 60);
  if (minutes >= 60) {
    return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
  }
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

/** The first sentence of a machine error, short enough for one banner line. */
export function shortReason(reason: string | null | undefined, max = 90): string | null {
  const text = reason?.replace(/\s+/g, " ").trim();
  if (!text) return null;
  const sentence = text.split(/(?<=[.!?])\s/)[0] ?? text;
  const clipped = sentence.length > max ? `${sentence.slice(0, max - 1).trimEnd()}…` : sentence;
  return clipped.replace(/[.!?]+$/, "");
}

/** Whole days an answer is late: from the start of the reply-by day. */
function daysLate(answerBy: string | null | undefined, now: number): number {
  if (!answerBy) return 0;
  const due = new Date(answerBy).getTime();
  if (Number.isNaN(due)) return 0;
  return Math.max(1, Math.floor((now - due) / DAY_MS));
}

function daysLeft(answerBy: string | null | undefined, now: number): number | null {
  if (!answerBy) return null;
  const due = new Date(answerBy).getTime();
  if (Number.isNaN(due)) return null;
  return Math.max(0, Math.ceil((due - now) / DAY_MS));
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const PLATFORM_NAME: Record<string, string> = {
  TIKTOK: "TikTok",
  INSTAGRAM: "Instagram",
  FACEBOOK: "Facebook",
  YOUTUBE: "YouTube",
  LINKEDIN: "LinkedIn",
  PINTEREST: "Pinterest",
  SNAPCHAT: "Snapchat",
  X: "X",
};
const platformName = (platform: string) => PLATFORM_NAME[platform] ?? platform;

/** The stage a person sees, from the client's sign-off rather than the tables. */
export function deriveStage(
  slot: PostStateSlot,
  brief: PostStateBrief | null | undefined,
): { stage: PostStage; stageIndex: 0 | 1 | 2; stops: 2 | 3 } {
  const delivered = !!slot.deliverable;
  // A quick post skipped the concept and the brief, and it never claims an
  // approval (Figma 08, 13 Oct): its two stops are the files, then Live.
  if (isQuickPost(slot)) {
    return delivered
      ? { stage: "live", stageIndex: 1, stops: 2 }
      : { stage: "production", stageIndex: 0, stops: 2 };
  }
  if (delivered) return { stage: "live", stageIndex: 2, stops: 3 };
  // The client approves the idea and its brief in one answer. A brief accepted
  // by the team on record counts the same; a card without the brief read
  // trusts the post's own status.
  const approved =
    slot.status === "CLIENT_APPROVED" &&
    (!brief || brief.status === "ACCEPTED" || brief.acceptanceSource === "TEAM");
  return approved
    ? { stage: "production", stageIndex: 1, stops: 3 }
    : { stage: "concept", stageIndex: 0, stops: 3 };
}

/** "5 posts, 3 need you" for the date rail's month block. */
export function monthCountLine(total: number, needYou: number): string {
  const posts = `${total} ${total === 1 ? "post" : "posts"}`;
  if (total === 0 || needYou === 0) return posts;
  if (needYou === total) return `${posts}, ${total === 1 ? "it needs you" : "all need you"}`;
  return `${posts}, ${needYou} ${needYou === 1 ? "needs" : "need"} you`;
}

// --------------------------------------------------------------- the model

type Turn = PostState["turn"];
type Row = { turn: Turn; banner: PostBanner; showBanner?: boolean };

/** Who approved and when: "Noura, Fatafeat · Wed 2 Dec, 16:20". */
function approvedLine(slot: PostStateSlot, client: string, timeZone?: string): string {
  const decision = slot.reviewDecisions?.[0];
  if (slot.approvalSource !== "TEAM" && decision?.decision === "APPROVED") {
    const who = decision.clientName?.trim();
    const at = dateTime(decision.decidedAt, timeZone);
    return [who ? `${who}, ${client}` : client, at].filter(Boolean).join(" · ");
  }
  const outside = slot.outsideApproval?.concept;
  if (outside) {
    return [`${outside.approvedByName}, ${client}`, dateTime(outside.approvedAt, timeZone)].filter(Boolean).join(" · ");
  }
  return `the team for ${client}`;
}

/** The post's channels as the cards read them, once its files are in. */
function liveTurn(slot: PostStateSlot, now: number, timeZone?: string): Turn {
  const pubs = (slot.deliverable?.publications ?? []).filter((p) => p.status !== "CANCELLED");
  if (pubs.length === 0) return { owner: "you", label: "Files in" };
  const live = pubs.filter((p) => p.status === "PUBLISHED");
  if (live.length === pubs.length) {
    const first = live.map((p) => p.publishedAt).filter((at): at is string => !!at).sort()[0];
    return { owner: "none", label: first ? `Live since ${whenOnCard(first, now, timeZone).replace(/^today /, "")}` : "Live" };
  }
  const failed = pubs.find((p) => p.status === "FAILED" && !p.nextAttemptAt);
  if (failed) return { owner: "you", label: `Didn't post on ${platformName(failed.platform)}`, alert: true };
  if (pubs.some((p) => p.status === "PUBLISHING")) return { owner: "agent", label: "Posting now" };
  const scheduled = pubs
    .filter((p) => p.status === "SCHEDULED" || (p.status === "FAILED" && p.nextAttemptAt))
    .map((p) => p.nextAttemptAt ?? p.scheduledAt)
    .filter((at): at is string => !!at)
    .sort();
  if (scheduled.length > 0 && scheduled.length + live.length === pubs.length) {
    return { owner: "none", label: `Scheduled · ${whenOnCard(scheduled[0]!, now, timeZone)}` };
  }
  if (live.length > 0) return { owner: "you", label: `Live on ${live.length} of ${pubs.length}` };
  return { owner: "you", label: "Files in" };
}

function teamRow(slot: PostStateSlot, options: PostStateOptions, stage: PostStage): Row {
  const Client = clientTitle(options.clientName);
  const brief = options.brief;
  const decline = openDecline(slot);
  const tz = options.timeZone;

  if (slot.status === "MISSED") {
    const day = shortDate(slot.scheduledAt);
    return {
      turn: { owner: "you", label: "Missed", alert: true },
      banner: {
        icon: "CalendarX",
        title: day ? `Missed · no idea picked by ${day}` : "Missed · no idea picked in time",
        meta: "Missed · nothing was sent. The three ideas stay for another day.",
        primary: { label: "Move", action: "move-post" },
      },
    };
  }

  // A decline from before the concept and brief went out together reopens the
  // post (PLANNED, or IDEATION with the reviewed idea back in play).
  if (decline && (slot.status === "PLANNED" || slot.status === "IDEATION")) {
    const reason = decline.reason?.trim();
    return {
      turn: { owner: "you", label: "Rework the idea" },
      banner: {
        icon: "MessageSquare",
        title: `${Client} asked for changes`,
        meta: reason ? `Concept · “${reason}”` : "Concept · Read their notes on the concept",
        primary: { label: "Rework the idea", action: "rework-idea" },
      },
    };
  }

  switch (slot.status) {
    case "PLANNED": {
      if (slot.ideaError) {
        const reason = shortReason(slot.ideaError);
        return {
          turn: { owner: "you", label: "Write ideas" },
          banner: {
            icon: "RotateCcw",
            title: "The ideas could not be written",
            meta: `Concept · ${reason ? `${reason}. ` : ""}Try again or ask in the chat`,
            primary: { label: "Try again", action: "retry-ideas" },
          },
          showBanner: false,
        };
      }
      return {
        turn: { owner: "you", label: "Write ideas" },
        banner: {
          icon: "Sparkles",
          title: "Ideas not written yet",
          meta: "Concept · Creo writes three ideas for this post",
          primary: { label: "Write ideas", action: "generate-ideas" },
        },
        showBanner: false,
      };
    }
    case "GENERATING_IDEAS": {
      const clock = clockSince(slot.updatedAt, options.now);
      return {
        turn: { owner: "agent", label: clock ? `Creo writes the ideas · ${clock}` : "Creo writes the ideas" },
        banner: {
          icon: "Loader",
          title: "Creo writes the ideas",
          meta: "You can leave. The card and Home say when they are ready.",
        },
        showBanner: false,
      };
    }
    case "IDEATION": {
      const count = slot.ideas?.length ?? 0;
      const selected = options.selectedIdea ?? null;
      const label = `${count} ${count === 1 ? "idea" : "ideas"}, pick one`;
      return {
        turn: { owner: "you", label },
        banner: {
          icon: "ListChecks",
          title: label,
          meta: `Concept · Pick one, and Creo writes its creative brief. ${Client} sees the picked idea and its brief together`,
          primary: selected
            ? { label: `Pick idea ${selected.index + 1}`, action: "pick-idea" }
            : { label: "Pick an idea", action: "pick-idea", disabled: true },
        },
        showBanner: false,
      };
    }
    case "SIGNED_OFF":
      return pickedRow(slot, options);
    case "IN_CLIENT_REVIEW":
      return withClientRow(slot, options);
    default:
      break;
  }

  // CLIENT_APPROVED: the idea (and with it, the brief) is approved.
  if (stage === "live") {
    return liveRow(options) ?? { turn: liveTurn(slot, options.now, tz), banner: filesInRow().banner, showBanner: false };
  }

  // A month from before the pair went out together: the brief still has its own loop.
  if (brief && brief.status !== "ACCEPTED" && brief.acceptanceSource !== "TEAM") {
    const legacy = legacyBriefRow(brief, options);
    if (legacy) return legacy;
  }

  if (options.live) {
    const row = liveRow(options);
    if (row) return row;
  }

  return {
    turn: { owner: "you", label: "Approved · add the files" },
    banner: {
      icon: "CircleCheck",
      title: `Approved by ${approvedLine(slot, Client, tz)}`,
      meta: "Approved · add the files",
      primary: { label: "Add files", action: "open-live" },
    },
  };
}

/** Picked: Creo writes the brief, then the pair is ready to send (or back with changes). */
function pickedRow(slot: PostStateSlot, options: PostStateOptions): Row {
  const client = clientLabel(options.clientName);
  const Client = clientTitle(options.clientName);
  const brief = options.brief;
  if (brief === undefined) {
    // Not asked yet (a card without the brief read): the pick is what is known.
    return {
      turn: { owner: "you", label: "Ready to send" },
      banner: { icon: "Send", title: "Ready to send", meta: "Idea and brief ready.", primary: { label: "Send", action: "send-concept" } },
    };
  }
  if (!brief || brief.status === "GENERATING") {
    const clock = clockSince(brief?.updatedAt ?? slot.updatedAt, options.now);
    return {
      turn: { owner: "agent", label: clock ? `Creo writes the brief · ${clock}` : "Creo writes the brief" },
      banner: {
        icon: "Loader",
        title: "Creo is writing the creative brief · about a minute",
        meta: "You can leave: the card and Home say when it’s ready. Not done after 3 minutes? Try again, or pick another idea.",
      },
      showBanner: false,
    };
  }
  if (brief.status === "FAILED") {
    const reason = shortReason(brief.generationError);
    return {
      turn: { owner: "you", label: "Fix the brief" },
      banner: {
        icon: "RotateCcw",
        title: "The brief could not be written",
        meta: reason ? `Concept · ${reason}` : "Concept · Try again, or write it yourself",
        primary: { label: "Try again", action: "retry-brief" },
      },
    };
  }
  const decision = slot.reviewDecisions?.[0];
  const sent = brief.sentVersion ?? 0;
  if (decision?.decision === "DECLINED" && (brief.status === "CHANGES_REQUESTED" || sent > 0)) {
    const at = dateTime(decision.decidedAt, options.timeZone);
    const comments = options.changeComments ?? 0;
    const reason = decision.reason?.trim();
    const what = comments > 0 ? `${plural(comments, "comment")} on the slide. ` : reason ? `“${shortReason(reason, 120)}” ` : "";
    return {
      turn: { owner: "you", label: "Changes asked" },
      banner: {
        icon: "MessageSquare",
        title: at ? `Changes asked · ${Client} answered ${at}` : "Changes asked",
        meta: `${what}Fix, then send version ${sent + 1}.`,
        primary: { label: `Send version ${sent + 1}`, action: "send-concept" },
      },
    };
  }
  if (sent > 0) {
    // Pulled back before the client answered: it goes again as it is, or edited.
    return {
      turn: { owner: "you", label: "Ready to send" },
      banner: {
        icon: "Send",
        title: "Ready to send",
        meta: `Written, not sent to ${client} yet.`,
        primary: { label: `Send to ${client}`, action: "send-concept" },
      },
    };
  }
  return {
    turn: { owner: "you", label: "Ready to send" },
    banner: { icon: "Send", title: "Ready to send", meta: "Idea and brief ready.", primary: { label: "Send", action: "send-concept" } },
  };
}

/** With the client: by when, how late, reminded and opened. */
function withClientRow(slot: PostStateSlot, options: PostStateOptions): Row {
  const client = clientLabel(options.clientName);
  const Client = clientTitle(options.clientName);
  const review = slot.clientReview?.concept ?? null;
  const tz = options.timeZone;
  const opened = review?.openedAt ? ` Opened ${dateTime(review.openedAt, tz)}.` : "";
  const sent = shortDate(review?.sentAt ?? options.conceptSentAt);
  if (review?.late && review.answerBy) {
    const late = daysLate(review.answerBy, options.now);
    const reminded = review.lastReminder?.sentAt ? ` Reminded ${dateTime(review.lastReminder.sentAt, tz)}.` : "";
    return {
      turn: { owner: "late", label: `With ${client} · ${plural(late, "day")} late` },
      banner: {
        icon: "Clock",
        title: `With ${Client} · ${plural(late, "day")} late`,
        tone: "alert",
        meta: `Reply was due ${shortDate(review.answerBy)}.${reminded}${opened}`,
        primary: { label: "Remind again", action: "remind" },
        secondary: { label: `Approve for ${client}`, action: "approve-for-client", quiet: true },
      },
    };
  }
  if (review?.answerBy) {
    const due = shortDate(review.answerBy);
    return {
      turn: { owner: "client", label: `With ${client} · by ${due}` },
      banner: {
        icon: "Clock",
        title: `With ${Client} · by ${due}`,
        meta: `The idea and its brief${sent ? `, sent ${sent}` : ""}.${opened}`,
        primary: { label: `Remind ${client}`, action: "remind", quiet: true },
      },
    };
  }
  return {
    turn: { owner: "client", label: `With ${client}` },
    banner: {
      icon: "Clock",
      title: `With ${Client}`,
      meta: `The idea and its brief${sent ? `, sent ${sent}` : ""}.${opened}`,
      primary: { label: `Remind ${client}`, action: "remind", quiet: true },
    },
  };
}

/** The brief's own loop, for posts approved before the pair went out together. */
function legacyBriefRow(brief: PostStateBrief, options: PostStateOptions): Row | null {
  const client = clientLabel(options.clientName);
  const Client = clientTitle(options.clientName);
  switch (brief.status) {
    case "GENERATING":
      return {
        turn: { owner: "agent", label: "Creo writes the brief" },
        banner: {
          icon: "Loader",
          title: "Creo is writing the creative brief · about a minute",
          meta: "You can leave: the card and Home say when it’s ready.",
        },
        showBanner: false,
      };
    case "FAILED":
      return {
        turn: { owner: "you", label: "Fix the brief" },
        banner: {
          icon: "RotateCcw",
          title: "The brief could not be written",
          meta: shortReason(brief.generationError) ?? "Try again, or write it yourself",
          primary: { label: "Try again", action: "retry-brief" },
        },
      };
    case "DRAFT": {
      const sent = brief.sentVersion ?? 0;
      return {
        turn: { owner: "you", label: "Ready to send" },
        banner: {
          icon: "Send",
          title: sent > 0 && brief.version > sent ? `Version ${brief.version} is ready to send` : "Ready to send",
          meta: sent > 0 ? `${Client} has version ${sent} of the brief.` : `The brief, not sent to ${client} yet.`,
          primary: { label: sent > 0 ? `Send version ${brief.version}` : "Send", action: "send-brief" },
        },
      };
    }
    case "IN_REVIEW": {
      const sent = shortDate(options.briefSentAt);
      return {
        turn: { owner: "client", label: `With ${client}` },
        banner: { icon: "Clock", title: `With ${Client}`, meta: sent ? `The creative brief, sent ${sent}.` : "The creative brief." },
      };
    }
    case "CHANGES_REQUESTED": {
      const sent = brief.sentVersion ?? 0;
      if (sent > 0 && brief.version > sent) {
        return {
          turn: { owner: "you", label: "Ready to send" },
          banner: {
            icon: "Send",
            title: `Version ${brief.version} is ready to send`,
            meta: `${Client} asked for changes on version ${sent}.`,
            primary: { label: `Send version ${brief.version}`, action: "send-brief" },
          },
        };
      }
      const reason = options.briefDeclineReason?.trim();
      return {
        turn: { owner: "you", label: "Changes asked" },
        banner: {
          icon: "MessageSquare",
          title: "Changes asked",
          meta: reason ? `“${shortReason(reason, 120)}” Fix, then send version ${sent + 1}.` : `Fix, then send version ${sent + 1}.`,
          primary: { label: "Edit the brief", action: "edit-brief" },
        },
      };
    }
    default:
      return null;
  }
}

/**
 * The client's side of the same post (Figma 08, 5 Client review). The banner
 * carries no primary here: Approve and Ask for a change live on the page.
 */
function guestRow(slot: PostStateSlot, options: PostStateOptions, stage: PostStage): Row {
  const review = slot.clientReview?.concept ?? null;
  const waiting = (title: string, meta: string, late = false): Row => ({
    turn: { owner: late ? "late" : "you", label: "Waiting for you" },
    banner: { icon: "MessageSquare", title, meta },
  });
  const withTeam = (meta: string): Row => ({
    turn: { owner: "client", label: "With the team" },
    banner: { icon: "MessageSquare", title: "With the team", meta },
  });

  if (slot.status === "IN_CLIENT_REVIEW") {
    const sent = shortDate(review?.sentAt ?? options.conceptSentAt);
    const due = shortDate(review?.answerBy);
    if (review?.late && review.answerBy) {
      const late = daysLate(review.answerBy, options.now);
      return waiting(
        `Waiting for you, by ${due} · ${plural(late, "day")} late`,
        `The idea and its brief${sent ? `, sent ${sent}` : ""}. One answer covers both. Approve, or ask for a change on any part.`,
        true,
      );
    }
    const left = daysLeft(review?.answerBy, options.now);
    return waiting(
      due ? `Waiting for you, by ${due}` : "Waiting for you",
      `${left !== null ? `${plural(left, "day")} left. ` : ""}The idea and its brief${sent ? `, sent ${sent}` : ""}.`,
    );
  }
  if (slot.status === "CLIENT_APPROVED" || stage !== "concept") {
    const decision = slot.reviewDecisions?.[0];
    const at = decision?.decision === "APPROVED" ? dateTime(decision.decidedAt) : null;
    const who = decision?.decision === "APPROVED" ? decision.clientName?.trim() : null;
    return {
      turn: { owner: "none", label: "Approved" },
      banner: {
        icon: "MessageSquare",
        title: "Approved",
        meta: `${at ? `Approved ${at}${who ? ` by ${who}` : ""}. ` : ""}The team adds the files and schedules it.`,
      },
    };
  }
  const decision = slot.reviewDecisions?.[0];
  if (decision?.decision === "DECLINED") {
    const at = dateTime(decision.decidedAt);
    return withTeam(`${at ? `You asked for a change ${at}. ` : ""}The team is on it; you will get version 2 by email.`);
  }
  return withTeam("The team is preparing this post.");
}

/** Quick posts skip Concept: the files first, then Live. */
function quickRow(slot: PostStateSlot, stage: PostStage, options: PostStateOptions): Row {
  if (options.live && !options.isGuest) {
    const row = liveRow(options);
    // A quick post never claims an approval (Figma 08, 13 Oct): the Live rows
    // that open with "Approved" read "Add the files" here.
    if (row) {
      return row.turn.label.startsWith("Approved")
        ? {
            ...row,
            turn: { ...row.turn, label: "Add the files" },
            banner: { ...row.banner, title: "Add the files", meta: row.banner.meta.replace(/^Approved · /, "") },
          }
        : row;
    }
  }
  if (stage === "live") {
    return { turn: liveTurn(slot, options.now, options.timeZone), banner: filesInRow().banner, showBanner: false };
  }
  return {
    turn: { owner: "you", label: "Add the files" },
    banner: {
      icon: "Package",
      title: "Add the files",
      meta: "Drop every file for this post in one place, all at once. Creo works out which channels each one fits.",
      primary: { label: "Add files", action: "open-live" },
    },
  };
}

/** A post in Live whose Live tab has not been read: no banner until it has. */
function filesInRow(): Row {
  return {
    turn: { owner: "you", label: "Files in" },
    banner: {
      icon: "Package",
      title: "Files in",
      meta: "The files and captions are on the Live tab.",
      primary: { label: "Open Live", action: "open-live" },
    },
    showBanner: false,
  };
}

/**
 * The Live tab's banners (Figma 08, the x50000 Live frames and "Files, going
 * out and live"): add the files, uploading, files in, scheduled, posting, a
 * channel that didn't post, one that needs reconnecting, a post-it-yourself
 * channel whose time came, and live. The first 24 hours' page has its own head.
 */
function liveRow(options: PostStateOptions): Row | null {
  const live = options.live;
  if (!live) return null;
  const tz = options.timeZone;
  const ch = live.channels ?? null;
  const files = (n: number) => `${n} ${n === 1 ? "file" : "files"}`;

  if (live.uploading > 0) {
    const total = Math.max(live.uploadTotal, live.uploading + live.filesIn);
    const landed = Math.max(0, total - live.uploading);
    return {
      turn: { owner: "agent", label: "Uploading" },
      banner: {
        icon: "Package",
        title: landed > 0 ? `Uploading ${live.uploading} of ${total} files` : `Uploading ${files(total)}`,
        meta: "They keep uploading if you leave this page. Creo sets each file’s channels as it lands.",
        primary: { label: "Add files", action: "add-files", quiet: true },
      },
    };
  }
  if (live.question) {
    return {
      turn: { owner: "you", label: "Answer Creo" },
      banner: { icon: "CircleHelp", title: "Creo needs one answer", meta: live.question },
    };
  }
  if (live.filesIn === 0) {
    return {
      turn: { owner: "you", label: "Approved · add the files" },
      banner: {
        icon: "Package",
        title: "Approved · add the files",
        meta: "Drop every file for this post in one place, all at once. Creo works out which channels each one fits.",
        primary: { label: "Add files", action: "add-files" },
      },
    };
  }
  if (live.results) {
    return { turn: { owner: "none", label: "Live" }, banner: { icon: "Package", title: "Live", meta: "The first 24 hours are in" }, showBanner: false };
  }
  if (!ch) return filesInRow();

  if (ch.selfDue) {
    const others = ch.total - 1;
    return {
      turn: { owner: "you", label: `${ch.selfDue} is yours to post` },
      banner: {
        icon: "Package",
        title: `${ch.selfDue} is yours to post now`,
        meta: `${others > 0 && ch.live > 0 ? `Creo posted the other ${others === 1 ? "one" : plural(others, "channel")}${ch.goesOutAt ? ` at ${clockOf(ch.goesOutAt, tz)}` : ""} and reminded you. ` : ""}Download the file, copy the caption, post it, then mark it as posted.`,
        primary: { label: "Mark as posted", action: "mark-posted" },
      },
    };
  }
  if (ch.failed && !ch.failed.nextAttemptAt) {
    const name = platformName(ch.failed.platform);
    return {
      turn: { owner: "you", label: `Didn't post on ${name}`, alert: true },
      banner: {
        icon: "Package",
        title: `Didn't post on ${name}${ch.live > 0 ? ` · live on ${ch.live} of ${ch.total}` : ""}`,
        meta: `Creo tried ${ch.failed.attempts} of 5 times. Try again now, or post it yourself.`,
        primary: { label: "Try again", action: "retry-post" },
      },
    };
  }
  if (ch.live === ch.total && ch.total > 0) {
    return {
      turn: { owner: "none", label: ch.liveSince ? `Live since ${clockOf(ch.liveSince, tz)}` : "Live" },
      banner: {
        icon: "Package",
        title: `Live on ${ch.live} of ${ch.total}${ch.liveSince ? ` · since ${shortDateIn(ch.liveSince, tz).split(" ")[0]} ${clockOf(ch.liveSince, tz)}` : ""}`,
        meta: "Numbers update hourly today, then daily for a week.",
      },
    };
  }
  if (ch.posting > 0) {
    return {
      turn: { owner: "agent", label: "Posting now" },
      banner: {
        icon: "Package",
        title: `Posting now · ${ch.live} of ${ch.total} live`,
        meta: "Each channel goes out within a minute. You can leave this page.",
      },
    };
  }
  if (ch.failed?.nextAttemptAt) {
    const name = platformName(ch.failed.platform);
    return {
      turn: { owner: "agent", label: `Trying ${name} again` },
      banner: {
        icon: "Package",
        title: `Didn't post on ${name}${ch.live > 0 ? ` · live on ${ch.live} of ${ch.total}` : ""}`,
        meta: `Creo tried ${ch.failed.attempts} of 5 times; the next try is at ${clockOf(ch.failed.nextAttemptAt, tz)}. Try again now, or post it yourself.`,
        primary: { label: "Try again", action: "retry-post", quiet: true },
      },
    };
  }
  if (ch.reconnect) {
    const name = platformName(ch.reconnect.platform);
    const others = ch.total - 1;
    return {
      turn: { owner: "you", label: `Reconnect ${name}` },
      banner: {
        icon: "Package",
        title: `${name} needs reconnecting · 1 of ${ch.total} cannot go out`,
        meta: `Reconnect ${ch.reconnect.handle ?? name} in Settings, Connections.${others > 0 ? ` The other ${others === 1 ? "one is" : `${others} are`} ready.` : ""}`,
        primary: { label: `Reconnect ${name}`, action: "reconnect" },
      },
    };
  }
  if (ch.scheduled > 0 && ch.scheduled + ch.live === ch.total) {
    const at = ch.goesOutAt;
    const all = ch.scheduled === ch.total ? (ch.total === 2 ? "both" : `all ${ch.total}`) : String(ch.scheduled);
    return {
      turn: { owner: "none", label: at ? `Scheduled · ${whenOnCard(at, options.now, tz)}` : "Scheduled" },
      banner: {
        icon: "Package",
        title: at ? `Scheduled, ${whenOnCard(at, options.now, tz)}` : "Scheduled",
        meta: `Creo posts ${ch.scheduled === 1 ? "it" : `${all} channels`}${at ? ` on ${dayClock(at, tz)}` : ""}. Unschedule takes the post back to not scheduled.`,
      },
    };
  }
  if (ch.notConnected.length > 0 && ch.ready === 0) {
    const n = ch.notConnected.length;
    return {
      turn: { owner: "you", label: "Connect the accounts" },
      banner: {
        icon: "Link2Off",
        title: n === 1 ? `${ch.notConnected[0]} isn’t connected` : `${n} accounts aren’t connected`,
        meta: `Connect ${ch.notConnected.join(" and ")} to schedule ${n === 1 ? "it" : n === 2 ? "both" : "them"}.`,
        primary: { label: n === 1 ? `Connect ${ch.notConnected[0]}` : n === 2 ? "Connect both" : "Connect them", action: "reconnect" },
      },
    };
  }
  if (ch.ready < ch.total) {
    const lines = [...ch.needs, ...ch.notConnected.map((name) => `${name} is not connected`)];
    return {
      turn: { owner: "you", label: "Files in" },
      banner: {
        icon: "Package",
        title: `Files in · ${ch.ready} of ${ch.total} channels ready`,
        meta: lines.length ? lines.map((line) => `${line}.`).join(" ") : "Some channels still need something.",
        primary: { label: "Add files", action: "add-files", quiet: true },
      },
    };
  }
  return {
    turn: { owner: "you", label: "Files in" },
    banner: {
      icon: "Package",
      title: "Files in · Ready, not scheduled yet",
      meta:
        ch.carouselPictures > 1
          ? `${plural(ch.carouselPictures, "picture")} go to the carousel channels, so Creo makes them one carousel. Drag them in Carousel order to change the order.`
          : "Creo set each file to the channels its shape fits. Check where it posts, then schedule it.",
      primary: { label: "Schedule", action: "schedule-all" },
    },
  };
}

export function derivePostState(slot: PostStateSlot, options: PostStateOptions): PostState {
  const { stage, stageIndex, stops } = deriveStage(slot, options.brief);
  const row = isQuickPost(slot)
    ? quickRow(slot, stage, options)
    : options.isGuest
      ? guestRow(slot, options, stage)
      : teamRow(slot, options, stage);
  return { stage, stageIndex, stops, quick: isQuickPost(slot), turn: row.turn, banner: row.banner, showBanner: row.showBanner ?? true };
}

// --------------------------------------------------- brief revision helpers

interface RevisionLike {
  version: number;
  sentRounds?: Array<{ roundNumber: number; sentAt: string }>;
  decisionsInRounds?: Array<{ decision: "APPROVED" | "DECLINED"; reason: string | null }>;
  decisionInRound?: { decision: "APPROVED" | "DECLINED"; reason: string | null } | null;
}

/** The latest time any version of the brief went to the client. */
export function briefSentAtFrom(revisions: readonly RevisionLike[] | undefined): string | null {
  let latest: string | null = null;
  for (const entry of revisions ?? []) {
    for (const round of entry.sentRounds ?? []) {
      if (!latest || round.sentAt > latest) latest = round.sentAt;
    }
  }
  return latest;
}

/** The client's reason on the brief version they sent back, else the newest decline. */
export function briefDeclineReasonFrom(
  revisions: readonly RevisionLike[] | undefined,
  sentVersion: number | null,
): string | null {
  const declines = (entry: RevisionLike) => {
    const all = entry.decisionsInRounds?.length
      ? entry.decisionsInRounds
      : entry.decisionInRound
        ? [entry.decisionInRound]
        : [];
    return all.filter((decision) => decision.decision === "DECLINED");
  };
  const ordered = [...(revisions ?? [])].sort((a, b) => b.version - a.version);
  const onSent = ordered.find((entry) => entry.version === sentVersion);
  const pick = (onSent && declines(onSent).at(-1)) ?? ordered.map((entry) => declines(entry).at(-1)).find(Boolean);
  return pick?.reason?.trim() || null;
}
